import { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Collapse,
  Divider,
  FormControlLabel,
  Grid,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import type {
  TransactionImportPreviewRowDTO,
  TransactionImportRowLineDTO,
  TransactionImportRowOverrideDTO,
} from '@inversor-hub/types';
import { ConfidenceChip } from './ConfidenceChip';
import { PreviewRowSplitEditor } from './PreviewRowSplitEditor';

interface PreviewRowDetailProps {
  row: TransactionImportPreviewRowDTO;
  override: TransactionImportRowOverrideDTO | undefined;
  onConceptChange: (concept: string) => void;
  onLinesChange: (lines: TransactionImportRowLineDTO[] | undefined) => void;
  onConfirmCategory?: () => void;
}

export const PreviewRowDetail = ({
  row,
  override,
  onConceptChange,
  onLinesChange,
  onConfirmCategory,
}: PreviewRowDetailProps) => {
  const concept = override?.concept ?? row.draft.concept ?? '';
  const hasConfidence = Boolean(row.draft.categoryConfidence);
  const hasWarnings = row.warnings.length > 0;
  const hasErrors = row.errors.length > 0;
  const splitRecommendation = row.draft.splitRecommendation?.shouldReview
    ? row.draft.splitRecommendation
    : undefined;

  const appliedLines = override?.lines;
  const isSplit = Boolean(appliedLines && appliedLines.length > 0);
  const total = row.draft.amount;
  const type = row.draft.type;
  const canSplit = type !== undefined && total !== undefined && total > 0;

  const [rawOpen, setRawOpen] = useState(false);
  const [splitOpen, setSplitOpen] = useState(isSplit || Boolean(splitRecommendation));

  // Keep the editor open whenever an actual split is applied (e.g. canonical
  // overrides returned after a preview refresh). This only forces it open — a
  // mere recommendation stays user-dismissable.
  useEffect(() => {
    if (isSplit) setSplitOpen(true);
  }, [isSplit]);

  const handleSplitToggle = (enabled: boolean) => {
    setSplitOpen(enabled);
    if (!enabled && isSplit) onLinesChange(undefined);
  };

  return (
    <Box sx={{ px: 3, py: 2, bgcolor: 'grey.50' }}>
      <Stack spacing={2}>
        {hasErrors && (
          <Stack spacing={1}>
            {row.errors.map((error) => (
              <Alert key={error} severity="error">
                {error}
              </Alert>
            ))}
          </Stack>
        )}

        {hasWarnings && (
          <Stack spacing={1}>
            {row.warnings.map((warning) => (
              <Alert key={warning} severity="warning">
                {warning}
              </Alert>
            ))}
          </Stack>
        )}

        {hasConfidence && row.draft.categoryConfidence && (
          <Stack direction="row" alignItems="center" gap={1} flexWrap="wrap">
            <Typography variant="caption" color="text.secondary">
              Categoría sugerida por la IA
            </Typography>
            <ConfidenceChip confidence={row.draft.categoryConfidence} />
            {onConfirmCategory && (
              <Button
                size="small"
                variant="outlined"
                color="success"
                startIcon={<CheckRoundedIcon />}
                onClick={onConfirmCategory}
              >
                Confirmar categoría
              </Button>
            )}
          </Stack>
        )}

        {splitRecommendation && !isSplit && (
          <Alert severity="info">
            <Typography variant="body2" fontWeight={600}>
              La IA recomienda repartir esta fila en dos categorías.
            </Typography>
            <Typography variant="body2">{splitRecommendation.reason}</Typography>
          </Alert>
        )}

        {canSplit && type && total !== undefined && (
          <Box>
            <FormControlLabel
              sx={{ mr: 0 }}
              control={
                <Switch
                  checked={splitOpen}
                  onChange={(event) => handleSplitToggle(event.target.checked)}
                />
              }
              label={
                <Box display="flex" alignItems="center" gap={0.5}>
                  Desglosar en dos categorías
                  <Tooltip
                    title="Un mismo movimiento puede pertenecer a dos categorías a efectos fiscales. Desglosarlo te ayuda a deducir correctamente cada parte."
                    arrow
                  >
                    <InfoOutlinedIcon
                      fontSize="small"
                      sx={{ color: 'text.disabled', cursor: 'help' }}
                    />
                  </Tooltip>
                </Box>
              }
            />
            <Collapse in={splitOpen} unmountOnExit timeout="auto">
              <Box mt={1.5}>
                <PreviewRowSplitEditor
                  total={total}
                  type={type}
                  suggestedCategories={splitRecommendation?.suggestedCategories}
                  value={appliedLines}
                  onApply={onLinesChange}
                  onClear={() => {
                    onLinesChange(undefined);
                    setSplitOpen(false);
                  }}
                />
              </Box>
            </Collapse>
          </Box>
        )}

        <Grid container spacing={2}>
          <Grid item xs={12}>
            <TextField
              label="Concepto"
              size="small"
              value={concept}
              onChange={(event) => onConceptChange(event.target.value)}
              fullWidth
            />
          </Grid>
        </Grid>

        <Divider />

        <Box>
          <Button
            size="small"
            variant="text"
            onClick={() => setRawOpen((current) => !current)}
            endIcon={rawOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            sx={{ pl: 0 }}
          >
            Ver datos originales del archivo
          </Button>
          {rawOpen && <RawOriginal original={row.original} />}
        </Box>
      </Stack>
    </Box>
  );
};

const RawOriginal = ({ original }: { original: Record<string, string> }) => {
  const entries = Object.entries(original).filter(([, value]) => value.trim() !== '');
  if (entries.length === 0) {
    return (
      <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
        No hay datos adicionales en el archivo.
      </Typography>
    );
  }
  return (
    <Box
      component="dl"
      sx={{
        mt: 1,
        mb: 0,
        display: 'grid',
        gridTemplateColumns: 'max-content 1fr',
        columnGap: 2,
        rowGap: 0.5,
        fontFamily: 'monospace',
        fontSize: '0.75rem',
      }}
    >
      {entries.map(([key, value]) => (
        <Box key={key} sx={{ display: 'contents' }}>
          <Box component="dt" sx={{ color: 'text.secondary', fontWeight: 600 }}>
            {key}
          </Box>
          <Box component="dd" sx={{ m: 0, color: 'text.primary', wordBreak: 'break-word' }}>
            {value}
          </Box>
        </Box>
      ))}
    </Box>
  );
};
